// ═══════════════════════════════════════════════════════════════════════════
// OcCarteiraPanel.jsx — a tela inicial do RiskMind OC, como COMPONENTE.
//
// De onde vem: painel_oc_inicio.template.html, o preview aprovado. Este arquivo
// é a porta desse preview para dentro do app. O que muda e o que NÃO muda está
// escrito abaixo, porque é justamente onde uma porta dessas costuma vazar.
//
// ── 1. O painel NÃO desenha cabeçalho ──────────────────────────────────────
// Nem a barra escura do app (marca, Reportar Erro / Sugerir Melhoria, idiomas,
// CLIENTE-SAIR), nem o lockup "RiskMind OC · Capital aberto". Os dois já são
// renderizados pelo sistema EM VOLTA deste bloco. Redesenhá-los aqui criaria uma
// segunda fonte para um cabeçalho que o produto já tem: bastaria o app trocar um
// texto, uma cor ou um botão para esta tela passar a exibir um cabeçalho
// diferente do resto do sistema, sem nada quebrar e sem ninguém perceber. Mesma
// classe da bolinha de legenda com a cor digitada à mão. O painel começa no que
// é dele: o contexto do plano e os indicadores.
//
// ── 2. O trocador de plano do preview NÃO existe aqui ──────────────────────
// No HTML havia um bloco [data-simulacao] com quatro botões trocando o plano
// exibido. Era ANDAIME: servia para o fundador ver os quatro cenários sem banco.
// Em produção o plano é um só — o que o tenant comprou — e vem do payload. O
// andaime não é "escondido", é ausente: chk_painel_oc.js reprova o arquivo se a
// string `data-simulacao` reaparecer.
//
// ── 3. Todo o CSS é escopado em .ocp ───────────────────────────────────────
// O preview era uma página inteira e por isso escrevia em `body`, `*`, `table`,
// `h2`, `footer`. Coladas num app, essas regras não decoram este painel: elas
// repintam o sistema inteiro — a tela de empresas fechadas herdaria a tabela e o
// rodapé daqui. Cada seletor passa a nascer sob a classe raiz `.ocp`, inclusive
// as custom properties (--navy e cia. saem de :root e vão para .ocp, o que de
// quebra impede que uma variável homônima do app vaze para dentro do painel).
// O gate confere seletor a seletor; não depende de eu lembrar.
//
// ── 4. O que NÃO mudou: os números e as decisões de leitura ────────────────
// Formatadores (nf/pc/pp/direcao/frase/dt/dth), a régua de HHI derivada da
// fronteira publicada pelo motor, o corte de k=0 do gráfico, o teto de 6
// movimentos, as cores das faixas de rating vindas das variáveis da paleta — tudo
// carregado com o mesmo comportamento. `esc()` sai porque JSX escapa por
// construção; é a única remoção, e ela é uma garantia mais forte, não mais fraca.
//
// PROPS
//   api          (path, init?) => Promise — o mesmo fetch do OcApp (Bearer +
//                X-Tenant-ID). Usado quando `dados` é nulo.
//   dados        payload já resolvido. Existe para o teste de render rodar o
//                JSON real do motor sem rede — e para o app poder pré-carregar.
//   onOpen       (cnpj) => void — abre a ficha da companhia. Sem ele, as linhas
//                não viram botão (nada de affordance que não leva a lugar nenhum).
//   onSelecionar () => void — leva à tela de escolha da carteira. Só é oferecido
//                no estado `sem_selecao`, e só se o app souber para onde ir.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   ESTILO — idêntico ao preview aprovado, com TODO seletor sob .ocp.

   Os valores foram lidos por computed style das telas em operação (universo OC
   e detalhe de companhia), não escolhidos aqui:
     família Inter · corpo 13px/400 · rótulo de KPI 10.5px caixa alta 0,4px
     valor de KPI 18px/700 navy-3 · card #fff raio 12px borda 1px SEM sombra
     chip raio 6px borda 1,5px na cor semântica · th 11.5px/600 · td 13px/400
   ═══════════════════════════════════════════════════════════════════════════ */
const CSS = String.raw`
.ocp{
  /* Geometria do histograma de eventos em UM lugar so. A malha (linhas-guia)
     e a area de plotagem sao a MESMA caixa: mesmo recuo de eixo, mesmo topo,
     mesma altura. Antes isso era coincidencia de tres pares de numeros
     magicos repetidos em regras diferentes (36px, 15px, 132px) — bastava
     mexer em um deles para a regua descolar da base das barras e o grafico
     passar a mentir. Agora e construcao: mudar --par-h move a malha junto. */
  --eixo-w:36px;  /* recuo do eixo Y:  .graf padding-left == .malha left   */
  --rot-h:15px;   /* rotulo da barra:  .rot height        == .malha top    */
  --par-h:200px;  /* area de plotagem: .par height        == .malha height */
  --navy:#0a1628; --navy-2:#162a48; --navy-3:#1a3f6f;
  --texto:#0f172a; --cinza:#6b7280;
  --laranja:#c87b2a; --ouro:#e8c56d; --creme:#faf7f2;
  --borda:#e5e7eb; --linha:#f1f5f9;
  --alerta:#dc2626; --atencao:#d97706; --positivo:#15803d; --neutro:#6b7280;
  --sans:Inter,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  background:var(--creme);color:var(--texto);font-family:var(--sans);
  font-size:13px;font-weight:400;-webkit-font-smoothing:antialiased;
}
.ocp,.ocp *{box-sizing:border-box}
.ocp .wrap{max-width:1180px;margin:0 auto;padding:0 28px}
/* h2 fica entre os dois papéis que o app define — 13/700 do título de card e
   20/800 da marca. Herda família, peso e cor de lá; só o tamanho interpola. */
.ocp h2{font-size:15px;font-weight:700;letter-spacing:-.2px;color:var(--navy);margin:0 0 4px}
.ocp .sub{font-size:12.5px;font-weight:400;color:var(--cinza);margin:0 0 18px;line-height:1.55}
.ocp section{padding:26px 0}
/* Números em Inter, como no app. O alinhamento de coluna que a monoespaçada
   dava vem daqui — a Inter tem figuras tabulares e elas bastam. */
.ocp .num,.ocp .valor,.ocp .fatos b,.ocp .rt,.ocp .pd,.ocp .delta,.ocp .seta,
.ocp .col b,.ocp .rot,.ocp .eixo-r em,.ocp .faixa .v,.ocp .rodape span,
.ocp .barra-rating div,.ocp .chip{font-variant-numeric:tabular-nums}

/* ── abertura ─────────────────────────────────────────────────────────
   Sem barra do app e sem lockup: ver o bloco 1 do cabeçalho deste arquivo. */
.ocp .abertura{padding:4px 0 0}

/* ── faixa de KPIs ────────────────────────────────────────────────── */
.ocp .faixa{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:20px}
.ocp .faixa div{background:#fff;border:1px solid var(--borda);border-radius:12px;padding:14px 16px}
.ocp .faixa .r{font-size:10.5px;font-weight:400;letter-spacing:.4px;text-transform:uppercase;
          color:var(--cinza);margin:0 0 7px}
.ocp .faixa .v{font-size:24px;font-weight:700;letter-spacing:-.4px;color:var(--navy-3);
          line-height:1;margin:0}
.ocp .faixa .d{font-size:12px;font-weight:400;color:var(--cinza);margin:8px 0 0;line-height:1.45}

/* ── cartões ──────────────────────────────────────────────────────── */
.ocp .grade{display:grid;gap:14px}
.ocp .g4{grid-template-columns:repeat(4,1fr)}
.ocp .g3{grid-template-columns:repeat(3,1fr)}
.ocp .g2{grid-template-columns:1fr 1fr}
.ocp .card{background:#fff;border:1px solid var(--borda);border-radius:12px;padding:16px 18px}
/* O app não usa sombra em lugar nenhum. Destaque aqui é borda mais grossa na
   cor de ênfase — o mesmo idioma do chip de rating. */
.ocp .card.destaque{border:1.5px solid var(--navy-3)}
.ocp .card h3{font-family:var(--sans);font-size:10.5px;font-weight:400;letter-spacing:.4px;
         text-transform:uppercase;color:var(--cinza);margin:0 0 11px}
.ocp .valor{font-size:26px;font-weight:700;letter-spacing:-.4px;line-height:1;color:var(--navy-3)}
.ocp .legenda{font-size:12.5px;font-weight:400;color:var(--cinza);margin-top:9px;line-height:1.5}
/* Chip do app: fundo transparente, borda 1,5px e texto na MESMA cor. */
.ocp .pill{display:inline-block;font-family:var(--sans);font-size:11px;font-weight:700;
      padding:1px 7px;border-radius:6px;border:1.5px solid;vertical-align:3px;margin-left:8px}
.ocp .pill.up{color:var(--atencao);border-color:var(--atencao)}
.ocp .pill.down{color:var(--positivo);border-color:var(--positivo)}
.ocp .pill.flat{color:var(--cinza);border-color:var(--borda)}

/* ── MIA ──────────────────────────────────────────────────────────── */
.ocp .mia{background:#fff;border:1px solid var(--borda);border-radius:12px;overflow:hidden}
.ocp .mia .cab{background:var(--navy);color:var(--creme);padding:13px 18px;display:flex;
          align-items:baseline;gap:12px;flex-wrap:wrap}
.ocp .mia .cab strong{font-size:15px;font-weight:800;letter-spacing:-.2px}
.ocp .mia .cab span{font-size:12px;font-weight:400;color:rgba(250,247,242,.7)}
.ocp .obs{display:grid;grid-template-columns:112px 1fr;gap:18px;padding:14px 18px;border-top:1px solid var(--linha)}
.ocp .obs:first-of-type{border-top:0}
.ocp .tag{font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
     padding:3px 0 3px 10px;border-left:3px solid;align-self:start;margin-top:2px}
.ocp .tag.alerta{color:var(--alerta);border-color:var(--alerta)}
.ocp .tag.atencao{color:var(--atencao);border-color:var(--atencao)}
.ocp .tag.positivo{color:var(--positivo);border-color:var(--positivo)}
.ocp .tag.neutro{color:var(--neutro);border-color:var(--neutro)}
.ocp .obs h4{font-size:13px;font-weight:700;margin:0 0 5px;color:var(--navy)}
.ocp .obs p{font-size:13px;font-weight:400;line-height:1.6;color:var(--cinza);margin:0}

/* ── gráficos ─────────────────────────────────────────────────────── */
.ocp .barra-rating{display:flex;height:38px;border-radius:6px;overflow:hidden;border:1px solid var(--borda)}
.ocp .barra-rating div{display:flex;align-items:center;justify-content:center;
  font-size:11.5px;font-weight:700;color:#fff;min-width:2px}
.ocp .escala{display:flex;justify-content:space-between;margin-top:10px;font-size:11.5px;
        font-weight:400;color:var(--cinza)}
.ocp .legenda-ig{display:flex;gap:16px;margin-top:12px;flex-wrap:wrap;font-size:12px;color:var(--cinza)}
.ocp .legenda-ig i{display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:6px;vertical-align:-1px}

/* ── régua de concentração ────────────────────────────────────────── */
/* Os rótulos de faixa são ancorados NO limite que nomeiam e crescem para a
   direita, cobrindo a faixa que ali começa. Distribuídos com space-between,
   "alta · 0,25" ia parar na ponta direita da régua — que vale 0,50 — e o
   desenho passava a contradizer o número impresso nele. */
.ocp .marca{position:relative;height:26px;margin-top:18px}
.ocp .marca i{position:absolute;bottom:0;width:2px;height:11px;background:var(--navy-3);border-radius:1px;
         transform:translateX(-1px)}
.ocp .marca b{position:absolute;bottom:13px;font-size:11.5px;font-weight:700;
         color:var(--navy-3);white-space:nowrap;transform:translateX(-50%);
         font-variant-numeric:tabular-nums}
.ocp .regua{position:relative;height:9px;border-radius:5px;display:flex;overflow:hidden}
.ocp .regua span{display:block;height:100%}
/* Tons contidos de propósito. A régua é uma ESCALA, não um veredito: quem dá o
   veredito é o marcador e a etiqueta ao lado do número. Com a faixa "alta" em
   laranja cheio, metade da largura do desenho gritava perigo numa carteira
   rotulada "baixa" — o olho lê área de cor antes de ler posição de marcador. */
/* Classe pura, sem prender à régua: a amostra dentro do rótulo "Piso do plano"
   usa ESTA MESMA regra. Antes o rótulo dizia "trecho cinza" — a cor morava no
   CSS e o nome dela, por extenso, morava no texto; bastava retonalizar a régua
   para o texto passar a mentir. */
.ocp .b0{background:#eef1f5} .ocp .b1{background:#dbe6f1}
.ocp .b2{background:#f5e3cb} .ocp .b3{background:#e8c9a4}
.ocp .eixo-r{position:relative;height:15px;margin-top:9px;font-size:11px;font-weight:400;color:var(--cinza)}
.ocp .eixo-r span{position:absolute;top:0;white-space:nowrap;border-left:1px solid var(--borda);padding-left:6px;height:11px}
.ocp .eixo-r span em{font-style:normal;color:var(--navy-3);font-weight:700;font-variant-numeric:tabular-nums}
.ocp .fatos{margin-top:18px;border-top:1px solid var(--borda)}
.ocp .fatos div{display:flex;justify-content:space-between;align-items:baseline;gap:16px;
           padding:10px 0;border-bottom:1px solid var(--linha);font-size:12.5px}
.ocp .fatos div:last-child{border-bottom:0}
.ocp .fatos span{color:var(--cinza);font-weight:400}
.ocp .fatos span i{display:inline-block;width:9px;height:9px;border-radius:2px;
              margin-right:7px;border:1px solid var(--borda)}
.ocp .fatos b{font-size:12.5px;font-weight:700;color:var(--navy-3);text-align:right}

/* O gráfico de colunas ocupa a largura inteira, mas as barras NÃO se espalham
   por ela: o texto que explica o número vai para uma coluna à esquerda e as
   colunas ficam à direita com largura contida. Antes o gráfico dividia a linha
   com a barra de qualidade — que é uma FAIXA (quer largura, não altura) — e o
   cartão da faixa terminava com 241px de branco morto em todos os planos. O
   defeito não era o espaçamento: era o par. */
.ocp .g-graf{display:grid;grid-template-columns:200px minmax(0,1fr);gap:20px;align-items:center}

/* O rótulo de valor mora em uma FAIXA PRÓPRIA acima das barras. Quando ele
   flutuava sobre a barra correlacionada, a barra independente — mais alta
   justamente em k=1, o ponto que o gráfico existe para mostrar — passava por
   cima do número. Faixa separada torna a colisão impossível por construção. */
/* gap ZERO entre as colunas de propósito: a linha de base é a borda inferior de
   cada .par, e com espaçamento entre elas o eixo saía picotado, parecendo cinco
   gráficos e não um. As barras continuam separadas porque são centralizadas
   dentro de uma coluna larga — a folga vem do alinhamento, não do gap. */
.ocp .colunas{display:flex;align-items:flex-end;gap:0;margin-top:0}
.ocp .colunas.escalonado{padding-bottom:17px}
/* Eixo em duas linhas. Medido: coluna 43,1px, rotulo de faixa 50,3px —
   -7,2px de folga em seis pares. Escalonar dobra o passo (86,2px) e nao
   encurta rotulo nenhum. position:relative de proposito: margem mudaria
   a altura da coluna e, com align-items:flex-end, tiraria as barras da
   mesma base. O padding-bottom devolve ao cartao o espaco da 2a linha. */
.ocp .colunas.escalonado .col:nth-child(even) b{position:relative;top:17px}
.ocp .col{flex:1;display:flex;flex-direction:column;align-items:center;min-width:0}
.ocp .rot{height:var(--rot-h);line-height:var(--rot-h);font-size:10px;font-weight:500;color:var(--cinza);
     white-space:nowrap;overflow:hidden;text-overflow:clip}
.ocp .par{height:var(--par-h);width:100%;display:flex;align-items:flex-end;gap:5px;
     justify-content:center;border-bottom:1px solid var(--borda)}
.ocp .par i{display:block;width:38%;max-width:30px;border-radius:2px 2px 0 0;min-height:0}
/* Uma regra só serve a barra E a bolinha da legenda. Separadas, elas divergem
   na primeira troca de paleta e a legenda passa a mentir sobre o gráfico. */
.ocp .par i.ind,.ocp .legenda-ig i.ind{background:#c7d3e2}
.ocp .par i.cor,.ocp .legenda-ig i.cor{background:var(--navy-3)}
.ocp .col b{font-size:10px;font-weight:700;color:var(--navy-3);margin-top:9px;white-space:nowrap;letter-spacing:-.2px}
.ocp .col small{font-size:11px;font-weight:400;color:var(--cinza);margin-top:3px;text-align:center}
.ocp .graf{position:relative;padding-left:var(--eixo-w);margin-top:16px}
.ocp .malha{position:absolute;left:var(--eixo-w);right:0;top:var(--rot-h);height:var(--par-h);pointer-events:none}
.ocp .malha span{position:absolute;left:0;right:0;height:0;border-top:1px dashed var(--borda)}
.ocp .malha span em{position:absolute;right:100%;margin-right:7px;top:-8px;font-size:9.5px;font-style:normal;font-weight:500;line-height:16px;color:var(--cinza);white-space:nowrap}

/* ── tabelas (mesma régua da tabela do universo OC) ───────────────── */
.ocp table{width:100%;border-collapse:collapse;font-size:13px}
.ocp th{text-align:left;font-size:11.5px;font-weight:600;letter-spacing:.4px;text-transform:uppercase;
   color:var(--cinza);padding:0 12px 9px;border-bottom:1px solid var(--borda)}
.ocp th.r,.ocp td.r{text-align:right}
.ocp td{padding:11px 12px;border-bottom:1px solid var(--linha);vertical-align:middle;color:var(--texto)}
.ocp tbody tr:last-child td{border-bottom:0}
.ocp td.nome{font-weight:600;color:var(--texto)}
.ocp td .peq{font-size:12px;font-weight:400;color:var(--cinza);margin-top:2px}
.ocp .trilho{position:relative;height:7px;background:var(--linha);border-radius:4px;min-width:120px}
.ocp .trilho i{position:absolute;left:0;top:0;height:100%;background:var(--navy-3);border-radius:4px}
.ocp .trilho u{position:absolute;top:-4px;width:2px;height:15px;background:var(--laranja);border-radius:1px}
.ocp .delta{font-size:12.5px;font-weight:700}
.ocp .delta.pos{color:var(--alerta)} .ocp .delta.neg{color:var(--positivo)}

/* ── listas laterais ──────────────────────────────────────────────── */
.ocp .linha{display:flex;justify-content:space-between;align-items:center;gap:14px;
       padding:10px 0;border-bottom:1px solid var(--linha)}
.ocp .linha:last-child{border-bottom:0}
.ocp .linha .esq{min-width:0}
.ocp .linha .t{font-size:13px;font-weight:600;color:var(--texto);white-space:nowrap;
          overflow:hidden;text-overflow:ellipsis}
.ocp .linha .s{font-size:12px;font-weight:400;color:var(--cinza);margin-top:2px}
.ocp .linha .dir{text-align:right;white-space:nowrap}
.ocp .rt{font-size:12.5px;font-weight:700;color:var(--navy-3)}
.ocp .pd{font-size:12px;font-weight:400;color:var(--cinza);margin-top:2px}
.ocp .seta{font-size:12.5px;font-weight:700}
.ocp .seta.piora{color:var(--alerta)} .ocp .seta.melhora{color:var(--positivo)}

/* ── clique para a ficha ──────────────────────────────────────────────
   Só nasce quando o app passou onOpen E a linha tem cnpj. Affordance que não
   leva a lugar nenhum é pior que ausência de affordance: o gestor clica, nada
   acontece, e ele conclui que a tela está quebrada. */
.ocp .clicavel{cursor:pointer}
.ocp .linha.clicavel:hover{background:var(--linha)}
.ocp tr.clicavel:hover td{background:var(--linha)}
.ocp .clicavel:focus-visible{outline:2px solid var(--navy-3);outline-offset:-2px}

/* ── lista de companhias (sob demanda) ────────────────────────────── */
.ocp .abrir{font-family:var(--sans);font-size:13px;font-weight:600;background:var(--navy);color:var(--creme);
  border:0;border-radius:6px;padding:9px 18px;cursor:pointer}
.ocp .abrir:hover{background:var(--navy-3)}
.ocp .chip{display:inline-block;font-size:11px;font-weight:700;
      padding:1px 7px;border-radius:6px;border:1.5px solid var(--navy-3);
      background:transparent;color:var(--navy-3)}
.ocp .chip.hy{border-color:var(--laranja);color:var(--laranja)}

/* ── estados vazios (sem ciclo / sem seleção / erro / carregando) ──── */
.ocp .estado{background:#fff;border:1px solid var(--borda);border-radius:12px;
        padding:34px 30px;margin-top:24px;max-width:640px}
.ocp .estado h3{font-size:15px;font-weight:700;color:var(--navy);margin:0 0 8px;letter-spacing:-.2px}
.ocp .estado p{font-size:13px;font-weight:400;color:var(--cinza);line-height:1.6;margin:0 0 14px}
.ocp .estado p:last-child{margin-bottom:0}
.ocp .estado code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;
             background:var(--linha);padding:1px 5px;border-radius:4px;color:var(--navy-3)}

/* ── rodapé auditável ─────────────────────────────────────────────── */
.ocp .rodape{background:var(--navy);color:rgba(250,247,242,.55);padding:22px 0;margin-top:14px}
.ocp .rodape .wrap{display:flex;gap:28px;flex-wrap:wrap;font-size:12px;font-weight:400}
.ocp .rodape b{display:block;font-size:10px;letter-spacing:.4px;text-transform:uppercase;
         color:rgba(250,247,242,.45);font-weight:600;margin-bottom:4px}
.ocp .rodape span{color:var(--creme);font-size:12.5px;font-weight:500}

@media(max-width:980px){
  .ocp .g4,.ocp .g3{grid-template-columns:1fr 1fr} .ocp .g2{grid-template-columns:1fr}
  .ocp .g-graf{grid-template-columns:minmax(0,1fr);gap:20px}
  .ocp .faixa{grid-template-columns:1fr 1fr}
  .ocp h2{font-size:14px}
  .ocp .obs{grid-template-columns:1fr;gap:6px}
}
@media(max-width:620px){
  .ocp .g4,.ocp .g3,.ocp .faixa{grid-template-columns:1fr} .ocp .wrap{padding:0 16px}
}
`;

/* ── formatação pt-BR — comportamento idêntico ao preview aprovado ────────── */
const nf = (v, d = 2) => v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
const pc = (v, d = 1) => v == null ? '—' : nf(100 * v, d) + '%';
const CASAS_PP = 2;
const pp = (v, d = CASAS_PP) => v == null ? '—' : (v > 0 ? '+' : '') + nf(100 * v, d) + ' p.p.';
/* Direção da etiqueta a partir do número IMPRESSO, não do bruto. pp() arredonda
   em CASAS_PP casas: um delta de +0,00008 saía escrito "+0,00 p.p." e pintado de
   laranja, como se a carteira tivesse piorado. Cor que contradiz o número que ela
   decora é a mesma classe da área de cor que contradiz o rótulo da régua — o olho
   lê a cor antes do número, então quem manda é o que está escrito. */
const direcao = (v, d = CASAS_PP) => v == null ? null
  : (Math.round(100 * v * Math.pow(10, d)) === 0 ? 'flat' : (v > 0 ? 'up' : 'down'));
/* A cor é uma AFIRMAÇÃO, não um enfeite. `direcao` já se recusa a pintar o que a
   tela imprime como zero; faltava recusar o que a estatística não sustenta.
   Quando o motor manda `delta_sig: false` — diferença dentro do erro padrão de
   população finita — a etiqueta CONTINUA mostrando o número (não se suprime
   medida) e perde a seta e a cor, que são a parte que afirma direção.
   `sig === undefined` (motor anterior) devolve o comportamento de hoje intacto. */
const direcaoSig = (v, sig) => (sig === false ? 'flat' : direcao(v));
/* Legenda do confronto com o universo. São CINCO situações que a frase única de
   antes tratava como duas, e as cinco levam a decisões diferentes:
     identidade      — a carteira É o universo; não existe grupo de fora.
     indeterminado   — n pequeno demais para o TCL socorrer PD assimétrica.
     sub_resolucao   — a diferença existe mas a tela a imprime como 0,00 p.p.
     nao_significante— |z| < 1,96: não se distingue de zero.
     acima / abaixo  — diferença sustentada; é o único caso que afirma direção.
   Sem `delta_status` (motor anterior) o `default` reproduz a frase de hoje. */
const legendaMercado = (r) => {
  const d = r.delta_vs_universo, ref = pc(r.pd_medio_universo, 2);
  switch (r.delta_status) {
    case 'identidade':
      return 'A carteira É o universo de capital aberto (' + r.delta_n_universo
        + ' companhias): não há grupo de fora contra o qual comparar.';
    case 'indeterminado':
      return 'Universo de capital aberto em ' + ref + '. Amostra de ' + r.delta_n
        + ' nome(s) — pequena demais para afirmar direção.';
    case 'sub_resolucao':
    case 'nao_significante':
      return 'Em linha com o universo de capital aberto (' + ref
        + '): a diferença não se distingue de zero.';
    default: break;
  }
  return d == null ? 'Sem referência de mercado nesta data-base.'
    : (d > 0 ? 'Acima' : 'Abaixo') + ' do universo de capital aberto ('
      + ref + ') em ' + pp(Math.abs(d)).replace('+', '');
};
/* Fecha a frase com UM ponto, nunca dois. "p.p." é abreviação: termina em ponto.
   Quem montava a frase somava o ponto final sem saber disso e a tela saía com
   "em 0,19 p.p..". A garantia mora onde o texto vira pixel, não na memória de
   quem escreve a próxima frase. */
const frase = (s) => s == null ? '' : String(s).replace(/\.*$/, '.');
const dt = (s) => !s ? '—' : String(s).slice(0, 10).split('-').reverse().join('/');
/* apurado_em vem "YYYY-MM-DD HH:MM" do SQL. Exibir em ISO ao lado de uma
   data-base em dd/mm/aaaa faria o rodapé parecer dois sistemas diferentes. */
const dth = (s) => { if (!s) return '—'; const [d, h] = String(s).split(' '); return dt(d) + (h ? ' · ' + h : ''); };

/* O tipo vem em minúscula e sem acento porque é CHAVE de gatilho, não texto de
   tela: mudar a chave quebraria os testes que asseguram a leitura da MIA. */
const ROTULO_TIPO = { alerta: 'Alerta', atencao: 'Atenção', positivo: 'Positivo', neutro: 'Contexto' };

/* Faixa de rating → cor. Grau de investimento em azul, especulativo em laranja:
   a fronteira BBB- é a única quebra que muda mandato e limite, então ela tem de
   ser visível sem ler legenda. As duas pontas da rampa são as VARIÁVEIS da
   paleta, não cópias do valor delas. Os passos do meio são interpolações e só
   existem como literal porque não são token de nada. */
const COR_FAIXA = {
  'AA+': 'var(--navy)', 'A': '#1e416b', 'BBB': '#3c6d9e',
  'BB': 'var(--laranja)', 'B': '#a85f1c', 'CCC-': '#7d3d10', 'N/D': '#98a2b0',
};
const IG_FAIXAS = ['AA+', 'A', 'BBB'];
/* A chave da faixa é o primeiro rating do grupo ("AA+" cobre de AAA a AA−).
   Exibida crua, o cliente lê como se fosse a nota exata de todo o grupo. */
const ROTULO_FAIXA = {
  'AA+': 'AAA a AA−', 'A': 'A+ a A−', 'BBB': 'BBB+ a BBB−', 'BB': 'BB+ a BB−',
  'B': 'B+ a B−', 'CCC-': 'CCC ou pior', 'N/D': 'Sem rating',
};
const faixaTxt = (f) => ROTULO_FAIXA[f] || f;

/* Ratings de grau de investimento, para o chip da lista de companhias. */
const IG = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-'];

const FATIA_ALTA = 0.25;
const TETO_MV = 6;

/* Nome do plano exibido ao cliente.
   O preview tinha um mapa de quatro chaves fixas — as do andaime de simulação.
   Produção manda `standalone_10..40` E `addon_10..40`, e o mapa não conhecia os
   add-ons: o cliente que comprou o OC como complemento do Fechadas leria
   "Plano undefined". O número de companhias é o MESMO campo que a tela já
   imprime em "Vagas do plano" (plano.limite), então ele é a fonte — um número
   só, num lugar só. O mapa fica como legenda de nome próprio quando existir. */
const NOME_PLANO = {
  standalone_10: '10 companhias abertas', standalone_20: '20 companhias abertas',
  standalone_40: '40 companhias abertas',
  addon_10: '10 companhias abertas', addon_20: '20 companhias abertas',
  addon_40: '40 companhias abertas',
  /* O interno não é um SKU: é a regra do login @financialmind.ai, que
     enxerga o universo inteiro. Entra no mapa em vez de virar um `if`
     solto porque o mecanismo de dar nome a plano já existe e é este. */
  interno: 'Universo completo (interno)',
};
const nomePlano = (plano) => {
  if (!plano) return '—';
  if (NOME_PLANO[plano.plan]) return NOME_PLANO[plano.plan];
  if (plano.limite != null) return plano.limite + ' companhias abertas';
  return plano.plan || '—';
};

// ═══════════════════════════════════════════════════════════════════════════
// Componente
// ═══════════════════════════════════════════════════════════════════════════
/* O que um payload SIGNIFICA mora aqui, e num lugar só.
   `dados` e a resposta do fetch são a mesma coisa entrando por portas
   diferentes — o app pode pré-carregar o resumo e passá-lo pronto. Enquanto
   cada porta decidia sozinha, a porta do `dados` mandava qualquer payload
   direto para Conteudo: um {status:'sem_ciclo'} pré-carregado ia ler
   plano.limite e quebrar a tela exatamente no cliente que ainda não tem
   vigência aberta — o caso que a produção tem hoje. Mesma classe da bolinha de
   legenda com a cor digitada à mão: duas fontes para uma decisão só. */
function faseDe(r) {
  const st = (r && r.status) || 'ok';
  if (st === 'sem_ciclo') return { fase: 'sem_ciclo' };
  if (st === 'sem_selecao') return { fase: 'sem_selecao', plano: (r && r.plano) || null };
  return { fase: 'ok', d: r };
}

export default function OcCarteiraPanel({ api, dados = null, onOpen, onSelecionar }) {
  const [estado, setEstado] = useState(() => (dados ? faseDe(dados) : { fase: 'carregando' }));

  useEffect(() => {
    if (dados) { setEstado(faseDe(dados)); return undefined; }
    if (typeof api !== 'function') { setEstado({ fase: 'erro', msg: 'Painel montado sem acesso à API.' }); return undefined; }
    let vivo = true;
    setEstado({ fase: 'carregando' });
    api('/api/oc/carteira/resumo').then((r) => {
      if (!vivo) return;
      setEstado(faseDe(r));
    }).catch((e) => {
      if (!vivo) return;
      setEstado({ fase: 'erro', status: e && e.status, msg: String((e && e.message) || e) });
    });
    return () => { vivo = false; };
  }, [api, dados]);

  return (
    <div className="ocp">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      {estado.fase === 'carregando' && <Estado titulo="Apurando a carteira…" texto="Lendo a última data-base do motor." />}
      {estado.fase === 'sem_ciclo' && (
        <Estado
          titulo="O RiskMind OC ainda não está liberado para esta empresa"
          texto={'A assinatura existe, mas a vigência do módulo de capital aberto ainda não foi ' +
            'aberta para o seu cadastro. Isso é feito pela nossa equipe em um clique — fale com ' +
            'o suporte informando o CNPJ da contratante e o acesso é liberado na hora.'} />
      )}
      {estado.fase === 'sem_selecao' && (
        <Estado
          titulo="Falta escolher as companhias da carteira"
          texto={'Seu plano acompanha ' + (estado.plano && estado.plano.limite != null
            ? estado.plano.limite + ' companhias' : 'companhias') +
            ' de capital aberto. A carteira ainda não foi montada — assim que você ' +
            'escolher os nomes, esta tela passa a mostrar a leitura de risco deles.'}
          acao={onSelecionar ? { rotulo: 'Escolher as companhias', fn: onSelecionar } : null} />
      )}
      {estado.fase === 'erro' && (
        <Estado
          titulo={estado.status === 403 ? 'Sem acesso ao módulo de capital aberto' : 'Não foi possível carregar a carteira'}
          texto={estado.status === 403
            ? 'Esta conta não tem o RiskMind OC habilitado. Se você contratou o módulo, avise o suporte para vincularmos o acesso.'
            : 'A leitura da carteira não respondeu agora. Recarregue a página em instantes; se persistir, use o "Reportar erro" no topo.'}
          detalhe={estado.msg} />
      )}
      {estado.fase === 'ok' && <Conteudo d={estado.d} onOpen={onOpen} />}
    </div>
  );
}

/** Estados vazios: sempre dizem o que aconteceu E o que fazer a respeito. */
function Estado({ titulo, texto, detalhe, acao }) {
  return (
    <div className="wrap">
      <div className="estado">
        <h3>{titulo}</h3>
        <p>{texto}</p>
        {acao ? <button type="button" className="abrir" onClick={acao.fn}>{acao.rotulo}</button> : null}
        {detalhe ? <p><code>{detalhe}</code></p> : null}
      </div>
    </div>
  );
}

function Conteudo({ d, onOpen }) {
  const [lista, setLista] = useState(false);
  const { plano, risco, setores, concentracao, destaques, cobertura, mia, auditoria } = d;

  /* Vira botão só quando o app sabe para onde levar E a linha carrega a chave. */
  const abrir = (cnpj) => (onOpen && cnpj ? {
    role: 'button', tabIndex: 0, title: 'Abrir a ficha desta companhia',
    onClick: () => onOpen(cnpj),
    onKeyDown: (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onOpen(cnpj); } },
  } : {});
  /* `undefined` em vez de string vazia: sem isto, os pontos que chamam
     cls('') emitem class="" no DOM quando a linha não é clicável. Aparar aqui
     — e não em cada chamada — mantém uma regra só; era a segunda cópia da
     mesma decisão que fazia dois dos quatro pontos divergirem. */
  const cls = (base, cnpj) => (base + (onOpen && cnpj ? ' clicavel' : '')).trim() || undefined;

  /* ── abertura ────────────────────────────────────────────────────────
     Saiu daqui a linha "N companhias sob monitoramento": ela morava ao lado do
     lockup "RiskMind OC", que é do sistema, e o MESMO plano.usados já aparece
     logo abaixo no indicador "Vagas do plano". Duas impressões do mesmo número
     em telas diferentes é como uma passa a divergir da outra. */
  const trocas = plano.trocas_permitidas - plano.trocas_usadas;
  /* No plano interno limite, vigência e trocas chegam null porque NÃO SE
     APLICAM — não porque acabaram. A diferença importa: `null - null` dá
     0, e 0 aqui faria a tela dizer que a janela de troca do ano já foi
     usada. Cada predicado pergunta pelo campo que a própria frase
     imprime, para nenhuma frase depender do nulo da vizinha. */
  const semLimite = plano.limite == null;
  const semVigencia = !plano.vigencia_fim;
  const kpis = [
    ['PD médio da carteira', pc(risco.pd_medio, 2), legendaMercado(risco)],
    ['Grau de investimento', pc(risco.grau_investimento, 0),
      'da carteira em BBB- ou melhor. ' + pc(1 - risco.grau_investimento, 0) + ' em grau especulativo.'],
    ['Concentração setorial', nf(concentracao.hhi, 2),
      'HHI ' + concentracao.rotulo + ' em ' + concentracao.n_setores + ' setores. Mínimo possível neste plano: '
        + nf(concentracao.hhi_minimo, 2) + '.'],
    semLimite
      ? ['Companhias monitoradas', nf(plano.usados, 0),
        'Universo completo de capital aberto coberto pelo OC. Este plano não tem limite de vagas.']
      : ['Vagas do plano', plano.usados + ' / ' + plano.limite,
        (plano.livres ? plano.livres + ' vaga(s) ainda livre(s). ' : 'Todas as vagas ocupadas. ')
          + (trocas > 0 ? trocas + ' troca(s) disponíveis nesta vigência.' : 'Janela de troca do ano já utilizada.')],
  ];

  /* ── cartões ─────────────────────────────────────────────────────── */
  const dpd = risco.delta_vs_universo;
  const cards = [
    { t: 'PD médio · carteira', v: pc(risco.pd_medio, 2),
      p: dpd == null ? null : { c: direcaoSig(dpd, risco.delta_sig), txt: pp(dpd) },
      l: 'Mediana ' + pc(risco.pd_mediano, 2) + '. Faixa de ' + pc(risco.pd_min, 2) + ' a ' + pc(risco.pd_max, 2) + '.' },
    { t: 'Eventos esperados · 12 meses', v: nf(risco.defaults_esperados, 2), destaque: true,
      l: 'No pior 1% dos cenários, ' + risco.q99_defaults + ' evento(s); no pior 5%, ' + risco.q95_defaults + '.' },
    { t: 'Chance de ao menos um evento', v: pc(risco.prob_ao_menos_um_cor, 1),
      l: 'Com correlação setorial. Sem correlação seria ' + pc(risco.prob_ao_menos_um_ind, 1)
        + '. Dois ou mais: ' + pc(risco.prob_dois_ou_mais_cor, 1) + '.' },
    { t: 'Cobertura do universo', v: pc(cobertura.pct, 1),
      l: cobertura.monitoradas + ' de ' + cobertura.universo_total + ' companhias abertas cobertas pelo motor.' },
  ];

  /* ── barra de rating ─────────────────────────────────────────────── */
  const dr = risco.distribuicao_rating;
  const nIG = dr.filter((f) => IG_FAIXAS.indexOf(f.faixa) >= 0).reduce((s, f) => s + f.n, 0);

  /* ── régua de concentração ───────────────────────────────────────────
     As fronteiras vêm do payload (limite_moderada/limite_alta), não daqui: a
     régua ilustra o rótulo calculado pelo motor e não pode discordar dele. O
     teto do eixo é DERIVADO da fronteira publicada, não escolhido: a faixa
     "alta" ocupa sempre o último quarto. Com teto no dobro de "alta", metade do
     desenho ficava reservada a uma região que nenhuma carteira do produto
     alcança — e essa metade morta era a mais carregada de cor. Sendo derivado, o
     eixo fica fixo entre data-bases: o gestor compara período contra período sem
     a régua se mexer por baixo dele. */
  const cc = concentracao;
  const LIM_M = cc.limite_moderada, LIM_A = cc.limite_alta, TETO = LIM_A / (1 - FATIA_ALTA);
  const posR = (v) => Math.max(0, Math.min(100, 100 * v / TETO));
  /* O piso do plano vira uma FAIXA CINZA no início da régua, e não um traço:
     aquele trecho é literalmente inalcançável com esta quantidade de empresas.
     Sem ele, um HHI de 0,14 parece mérito de gestão quando 0,13 já era o mínimo
     possível — o que a seleção controla é só a distância entre o fim do cinza e
     o marcador. */
  const piso = cc.hhi_minimo != null ? posR(cc.hhi_minimo) : 0;
  const fatos = [
    ['Setores representados', String(cc.n_setores)],
    /* Setores EFETIVOS (inverso do HHI) vem do motor, não de uma conta aqui. É o
       que traduz o índice para a linguagem do gestor: "7,7 de 8" diz na hora que
       a carteira já está equilibrada, coisa que 0,13 sozinho não comunica. */
    ['Setores efetivos', cc.setores_efetivos == null ? '—' : nf(cc.setores_efetivos, 1) + ' de ' + cc.n_setores],
    ['Maior exposição', (cc.top_setor || '—') + ' · ' + pc(cc.top_setor_pct, 0)],
    /* A amostra carrega a classe b0 — a mesma da faixa que ela nomeia. Quem
       retonalizar a régua retonaliza o rótulo junto, sem saber que existe. */
    [<><i className="b0" />Piso do plano · início da régua</>, cc.hhi_minimo == null ? '—' : nf(cc.hhi_minimo, 2)],
  ];

  /* ── colunas de default ──────────────────────────────────────────────
     k=0 sai do gráfico e vira número em destaque: com 84% da massa em zero, as
     demais barras virariam fios de cabelo e o gráfico não informaria nada sobre
     a cauda, que é o motivo de ele existir. */
  const serie = risco.distribuicao_defaults.filter((b) => b.k > 0);
  const p0 = risco.distribuicao_defaults.find((b) => b.k === 0);
  const topo = Math.max(...serie.map((b) => Math.max(b.p_cor, b.p_ind)), 1e-9);
  /* — a régua vertical ————————————————————————————————————————
     A massa vai de 0,04% na cauda a 76,6% no pico da série independente:
     três ordens de grandeza. Numa régua linear ancorada em 0,766 tudo
     abaixo de 1,149% cai no piso: os baldes de 0,52%, 0,21%, 0,08% e
     0,04% viravam quatro retângulos de 2,00px iguais, com o de 1,2% a
     0,11px de distância — a cauda, que é o motivo de o gráfico existir,
     era justamente a parte que a régua apagava.
     A régua é a RAIZ da probabilidade: transformação estabilizadora de
     variância de uma proporção, monótona, sem parâmetro, e que PRESERVA O
     ZERO — altura zero é probabilidade zero, sem piso artificial. O log
     abriria mais a cauda, mas exige escolher um chão arbitrário e não tem
     linha de base, o que numa barra é uma mentira gráfica.
     As duas séries dividem o MESMO topo: comparar as barras entre si
     continua sendo válido, que é o conteúdo do gráfico.
     O percentual impresso acima da barra é o valor exato; a barra carrega
     só a forma. */
  /* Escala do histograma: LINEAR. A altura da barra e a propria probabilidade
     em porcentagem do topo. Antes era 100*sqrt(p/topo), e a raiz fazia uma
     barra de 25,1% subir acima da linha de 50% — o numero impresso nao podia
     ser conferido contra a regua. O piso de 0,5% (1px em 200px) vale SO para
     a barra: probabilidade positiva nao pode desenhar altura zero, que leria
     como impossivel. A regua nunca recebe piso, porque linha-guia deslocada
     mente. Por isso sao duas funcoes, e altBarra e definida SOBRE escala. */
  const escala = (p, t) => (p > 0 && t > 0 ? (100 * p) / t : 0);
  const altBarra = (p, t) => (p > 0 && t > 0 ? Math.max(escala(p, t), 0.5) : 0);
  /* Regua uniforme, passo redondo (1, 2 ou 5 vezes uma potencia de 10). A
     regua antiga era a lista fixa 0,1% 1% 5% 20% 50%: espacamento irregular
     que so nao saltava aos olhos porque a raiz ja distorcia tudo. Com escala
     linear as linhas TEM de ser equidistantes, senao a distorcao volta pela
     janela. O laco e limitado em 20 voltas para que um topo degenerado (1e-9)
     nao trave a tela. As casas do rotulo saem do proprio passo, para que duas
     linhas vizinhas nunca aparecam com o mesmo numero. */
  const passoRegua = (t) => {
    const bruto = t / 8;
    const mag = Math.pow(10, Math.floor(Math.log10(bruto)));
    const n = bruto / mag;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
  };
  const passoY = passoRegua(topo);
  const casasY = Math.max(0, Math.min(4, -Math.floor(Math.log10(passoY))));
  const TICKS_Y = [];
  for (let k = 1; k <= 20 && k * passoY < topo; k += 1) TICKS_Y.push(k * passoY);
    const rotulos = serie.map((b) => b.rotulo || ((b.k_mais ? '≥ ' : '') + b.k));
    /* — o eixo horizontal ————————————————————————
       Medido na producao: coluna de 43,1px contra rotulo de faixa de
       50,3px — sobreposicao de 7,2px em seis pares seguidos. Escalonar em
       duas linhas dobra o passo para 86,2px sem encurtar nada.
       So escalona quando ha rotulo de FAIXA num eixo cheio; com baldes
       unitarios (0,1,2,…,≥9) o rotulo tem 4 caracteres e nao encosta.
       A decisao le o MESMO vetor `rotulos` que vai para a tela — assim a
       regra nao pode divergir do que esta desenhado. */
    const eixoEscalonado =
      rotulos.length > 8 && Math.max(...rotulos.map((r) => r.length)) >= 6;

  /* ── setores ─────────────────────────────────────────────────────── */
  const maxPd = Math.max(...setores.map((s) => Math.max(s.pd_medio || 0, s.pd_medio_universo || 0)), 1e-9);

  /* ── destaques ───────────────────────────────────────────────────────
     Piores e melhores usam o MESMO desenho de linha de propósito: a carteira tem
     as duas pontas, e mostrar só a cauda ruim faz o gestor ler a tela como um
     painel de problemas em vez de um retrato da carteira. */
  const LinhaEmp = ({ e }) => (
    <div className={cls('linha', e.cnpj)} {...abrir(e.cnpj)}>
      <div className="esq">
        <div className="t">{e.denom}</div>
        <div className="s">{e.setor}{e.sector_pct != null ? ' · percentil ' + e.sector_pct + ' do setor' : ''}</div>
      </div>
      <div className="dir">
        <div className="rt">{e.rating || '—'}</div>
        <div className="pd">{pc(e.pd, 2)}</div>
      </div>
    </div>
  );
  const vazio = <p className="legenda">Nenhuma empresa com PD apurada nesta data-base.</p>;

  /* Rebaixamentos primeiro e maior queda no topo — a mesma ordem da leitura da
     MIA, para o gestor não ter de reconciliar duas listas. Corta em 6 porque no
     plano de 40 a lista inteira empurra o resto da tela para baixo; o total
     continua declarado embaixo e na observação da MIA. */
  const mv = [...destaques.movimentos].sort((a, b) =>
    (a.direcao === 'piora' ? 0 : 1) - (b.direcao === 'piora' ? 0 : 1) || (b.degraus || 0) - (a.degraus || 0));

  /* ── companhias (sob demanda) ────────────────────────────────────── */
  const emp = setores.flatMap((s) => s.empresas.map((e) => ({ ...e, setor: s.setor })))
    .sort((a, b) => (b.pd || 0) - (a.pd || 0));

  return (
    <>
      <div className="abertura">
        <div className="wrap">
          <p className="sub" style={{ margin: 0 }}>
            {'Plano ' + nomePlano(plano) + '. '
              + (semVigencia ? 'Sem prazo de vigência. '
                : 'Vigência até ' + dt(plano.vigencia_fim) + ' — ' + plano.dias_restantes + ' dias restantes. ')
              + 'Posição apurada na data-base de ' + dt(auditoria.data_base) + '.'}
          </p>
          <div className="faixa">
            {kpis.map(([r, v, dd], i) => (
              <div key={i}><p className="r">{r}</p><p className="v">{v}</p><p className="d">{frase(dd)}</p></div>
            ))}
          </div>
        </div>
      </div>

      <section>
        <div className="wrap">
          <h2>Leitura da carteira</h2>
          <p className="sub">O que muda o risco desta carteira nesta data-base, em ordem de severidade.</p>
          <div className="mia">
            <div className="cab">
              <strong>MIA RISK</strong>
              <span>{mia.length + ' observações sobre ' + auditoria.empresas_apuradas
                + ' companhias apuradas · data-base ' + dt(auditoria.data_base)}</span>
            </div>
            <div>
              {mia.map((o, i) => (
                <div className="obs" key={i}>
                  <div className={'tag ' + o.tipo}>{ROTULO_TIPO[o.tipo] || o.tipo}</div>
                  <div><h4>{o.titulo}</h4><p>{o.texto}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="grade g4">
            {cards.map((c, i) => (
              <div className={'card' + (c.destaque ? ' destaque' : '')} key={i}>
                <h3>{c.t}</h3>
                <div className="valor">{c.v}{c.p ? <span className={'pill ' + c.p.c}>{c.p.txt}</span> : null}</div>
                <div className="legenda">{frase(c.l)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap grade g2" style={{ alignItems: 'start' }}>
          <div className="card">
            <h3>Qualidade de crédito</h3>
            <div className="barra-rating">
              {dr.map((f, i) => (
                <div key={i} style={{ width: (100 * f.pct) + '%', background: COR_FAIXA[f.faixa] || '#98a2b0' }}
                  title={faixaTxt(f.faixa) + ': ' + f.n}>{f.pct >= 0.09 ? f.n : ''}</div>
              ))}
            </div>
            <div className="escala">
              <span>{'Grau de investimento · ' + nIG + ' empresa(s)'}</span>
              <span>{(risco.n - nIG) + ' em grau especulativo'}</span>
            </div>
            <div className="legenda-ig">
              {dr.map((f, i) => (
                <span key={i}>
                  <i style={{ background: COR_FAIXA[f.faixa] || '#98a2b0' }} />
                  {faixaTxt(f.faixa) + ' · ' + f.n + ' (' + pc(f.pct, 0) + ')'}
                </span>
              ))}
            </div>
          </div>

          <div className="card">
            <h3>Concentração setorial</h3>
            <div className="valor">
              {nf(cc.hhi, 2)}
              {/* Três faixas, três cores. Antes era "baixa ? verde : laranja", o
                  que pintava MODERADA com a cor de ALTA — a etiqueta dizia uma
                  coisa e a cor dizia outra, e a cor chega primeiro. */}
              <span className={'pill ' + ({ baixa: 'down', moderada: 'flat', alta: 'up' }[cc.rotulo] || 'flat')}>
                {cc.rotulo || '—'}
              </span>
            </div>
            <div className="legenda">
              Índice Herfindahl-Hirschman sobre a participação de cada setor na carteira.
              Quanto menor, mais distribuída a exposição.
            </div>
            <div className="marca">
              <i style={{ left: posR(cc.hhi).toFixed(1) + '%' }} />
              <b style={{ left: posR(cc.hhi).toFixed(1) + '%' }}>{nf(cc.hhi, 2)}</b>
            </div>
            <div className="regua">
              {piso > 0 ? <span className="b0" style={{ width: piso.toFixed(1) + '%' }} /> : null}
              <span className="b1" style={{ width: Math.max(0, posR(LIM_M) - piso).toFixed(1) + '%' }} />
              <span className="b2" style={{ width: (posR(LIM_A) - posR(LIM_M)).toFixed(1) + '%' }} />
              <span className="b3" style={{ width: (100 - posR(LIM_A)).toFixed(1) + '%' }} />
            </div>
            <div className="eixo-r">
              <span style={{ left: posR(LIM_M).toFixed(1) + '%' }}>moderada · <em>{nf(LIM_M, 2)}</em></span>
              <span style={{ left: posR(LIM_A).toFixed(1) + '%' }}>alta · <em>{nf(LIM_A, 2)}</em></span>
            </div>
            <div className="fatos">
              {fatos.map(([r, v], i) => <div key={i}><span>{r}</span><b>{v}</b></div>)}
            </div>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="card">
            <h3>Eventos de default em 12 meses</h3>
            <div className="g-graf">
              <div>
                <div className="valor">{pc(p0 ? p0.p_cor : null, 1)}</div>
                <div className="legenda">de chance de a carteira passar doze meses sem nenhum
                  evento. As barras abrem o restante da probabilidade.</div>
                {/* As bolinhas da legenda usam AS MESMAS classes das barras
                    (.cor/.ind). Com a cor escrita à mão aqui, bastava mudar o
                    gráfico para a legenda passar a apontar para a cor errada sem
                    nada quebrar — foi o que aconteceu ao trocar a paleta da
                    landing pela do app. A única fonte da cor é a regra. */}
                <div className="legenda-ig" style={{ marginTop: 20, flexDirection: 'column', gap: 9 }}>
                  <span><i className="cor" />com correlação setorial — os % exibidos</span>
                  <span><i className="ind" />devedores independentes</span>
                </div>
              </div>
              <div className="graf">
                {/* Linhas-guia rotuladas. Régua não-linear sem eixo é armadilha;
                    com eixo, é informação. A posição de cada linha usa a MESMA
                    função das barras — se a régua mudar, a malha muda junto. */}
                <div className="malha">
                  {TICKS_Y.map((v, i) => (
                    <span key={i} style={{ bottom: escala(v, topo).toFixed(2) + '%' }}>
                      <em>{pc(v, casasY)}</em>
                    </span>
                  ))}
                </div>
                <div className={'colunas' + (eixoEscalonado ? ' escalonado' : '')}>
                  {serie.map((b, i) => (
                    <div className="col" key={i}>
                      <div className="rot">{pc(b.p_cor, b.p_cor < 0.01 ? 2 : 1)}</div>
                      <div className="par">
                        <i className="cor" style={{ height: altBarra(b.p_cor, topo).toFixed(2) + '%' }} />
                        <i className="ind" style={{ height: altBarra(b.p_ind, topo).toFixed(2) + '%' }} />
                      </div>
                      <b>{rotulos[i]}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="card">
            <h3>Composição e risco por setor</h3>
            <table>
              <thead><tr>
                <th>Setor</th><th className="r">Empresas</th><th className="r">% carteira</th>
                <th>PD médio do setor na carteira</th><th>Maior risco</th>
              </tr></thead>
              <tbody>
                {setores.map((s, i) => {
                  const w = 100 * (s.pd_medio || 0) / maxPd, wu = 100 * (s.pd_medio_universo || 0) / maxPd;
                  return (
                    <tr key={i}>
                      <td className="nome">{s.setor}</td>
                      <td className="r num">{s.n}</td>
                      <td className="r num">{pc(s.pct, 0)}</td>
                      <td>
                        <div className="trilho">
                          <i style={{ width: w.toFixed(1) + '%' }} />
                          {s.pd_medio_universo != null ? <u style={{ left: wu.toFixed(1) + '%' }} /> : null}
                        </div>
                        <div className="peq num">{pc(s.pd_medio, 2)}</div>
                      </td>
                      <td>
                        {s.pior ? (
                          <div className={cls('', s.pior.cnpj)} {...abrir(s.pior.cnpj)}>
                            <div className="nome" style={{ fontSize: 13.5 }}>{s.pior.denom}</div>
                            <div className="peq num">{s.pior.rating + ' · ' + pc(s.pior.pd, 2)}</div>
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="legenda" style={{ marginTop: 18 }}>A barra mede o PD médio das empresas da carteira
              naquele setor; o traço laranja marca o PD médio do mesmo setor no universo de capital aberto.</p>
          </div>
        </div>
      </section>

      {(() => {
        /* — Composição e risco por setor Risk ──────────────────────────────
           Segunda leitura setorial, do proprio Risk, ao lado da tabela que
           vem da fonte de dados (logo acima, intocada).

           Retorna null enquanto o backend nao publicar d.setor_risk: assim
           esta tela pode subir antes do backend sem quebrar nada. */
        const sr = d.setor_risk;
        if (!sr || !Array.isArray(sr.setores_risk) || !sr.setores_risk.length) return null;
        const linhasR = sr.setores_risk;
        const aud = sr.auditoria || {};
        const reg = aud.por_regra || {};
        const cob = d.cobertura || {};
        const maxR = Math.max.apply(null, linhasR.map((s) => s.pd_media || 0).concat([1e-12]));

        /* A carteira monitorada e, hoje, o proprio universo comparavel. Quando
           isso acontece, a media do setor no universo e por construcao IGUAL a
           media do setor na carteira: a coluna so teria zeros. Uma coluna de
           zeros — ou de travessoes — ocupa espaco e nao diz nada, entao ela
           nao e desenhada; a legenda explica a ausencia. A deteccao e MEDIDA,
           nao chutada: no dia em que o universo for maior que a carteira, a
           coluna reaparece sozinha, sem trocar uma linha de codigo. */
        const identidade = linhasR.every((s) => s.media_setor_risk == null || s.pd_media == null
          || Math.abs(s.media_setor_risk - s.pd_media) < 1e-9);

        const origensDe = (s) => (s.origens || [])
          .map((o) => o.setor_fonte + ' (' + o.n + ')').join(' · ');
        const vsComp = (s) => {
          const v = s.vs_complemento;
          return !v || v.d == null || v.status === 'indeterminado' ? '—' : pp(v.d);
        };
        const clsComp = (s) => {
          const v = s.vs_complemento;
          return 'delta ' + (!v || !v.sig || v.d == null ? '' : (v.d > 0 ? 'pos' : 'neg'));
        };
        const pDe = (s) => {
          const v = s.vs_complemento;
          if (!v || v.p == null) return '';
          return (v.p < 0.001 ? 'p < 0,001' : 'p = ' + nf(v.p, 3)) + ' · n = ' + v.n_a;
        };

        return (
          <section style={{ paddingTop: 0 }}>
            <div className="wrap">
              <div className="card">
                <h3>Composição e risco por setor Risk</h3>
                <p className="legenda" style={{ margin: '-6px 0 16px' }}>
                  {'Segunda leitura setorial, feita pelo Risk: as empresas de participação foram dissolvidas no setor '
                    + 'da operação que elas controlam, as que a fonte não classifica foram enquadradas pela atividade e '
                    + 'os setores próximos foram agrupados. A tabela acima, com o setor que vem da fonte de dados, '
                    + 'permanece exatamente como está. Passe o cursor sobre o nome do setor para ver de quais setores '
                    + 'da fonte ele foi formado.'}
                </p>
                <table>
                  <thead><tr>
                    <th>Setor Risk</th><th className="r">Empresas</th><th className="r">% carteira</th>
                    <th>PD do setor na carteira</th>
                    {identidade ? null : <th className="r">vs. mesmo setor no universo</th>}
                    <th className="r">vs. demais setores</th>
                  </tr></thead>
                  <tbody>
                    {linhasR.map((s, i) => (
                      <tr key={i}>
                        <td className="nome" title={origensDe(s)}>{s.setor_risk}</td>
                        <td className="r num">{s.n}</td>
                        <td className="r num">{pc(s.pct, 0)}</td>
                        <td>
                          <div className="trilho">
                            <i style={{ width: (100 * (s.pd_media || 0) / maxR).toFixed(1) + '%' }} />
                            {aud.media_mercado != null
                              ? <u style={{ left: Math.min(100, 100 * aud.media_mercado / maxR).toFixed(1) + '%' }} />
                              : null}
                          </div>
                          <div className="peq num">{'média ' + pc(s.pd_media, 2) + ' · mediana ' + pc(s.pd_mediana, 2)}</div>
                        </td>
                        {identidade ? null : (
                          <td className="r">
                            <span className="delta">{s.media_setor_risk == null || s.pd_media == null
                              ? '—' : pp(s.pd_media - s.media_setor_risk)}</span>
                            <div className="peq num">{s.n_universo ? 'n = ' + s.n_universo + ' no universo' : ''}</div>
                          </td>
                        )}
                        <td className="r">
                          <span className={clsComp(s)}>{vsComp(s)}</span>
                          <div className="peq num">{pDe(s)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="legenda" style={{ marginTop: 18 }}>
                  {'A barra mede o PD médio do setor Risk na carteira e o traço laranja marca o PD médio de toda a base ('
                    + pc(aud.media_mercado, 2) + '), que é a referência de mercado. A coluna “vs. demais setores” '
                    + 'compara o setor com todas as outras empresas da base por teste t de Welch, com variâncias '
                    + 'separadas e graus de liberdade de Welch–Satterthwaite; o p-valor vem da beta incompleta '
                    + 'regularizada, e não de aproximação normal, porque vários setores têm n de um dígito. Só ganha cor '
                    + 'o que é significante a α = ' + nf((aud.alfa != null ? aud.alfa : 0.05) * 100, 0) + '%.'}
                </p>
                {identidade ? (
                  <p className="legenda" style={{ marginTop: 8 }}>
                    {'Não há coluna de comparação com o mesmo setor no universo porque a carteira monitorada é hoje o '
                      + 'próprio universo comparável'
                      + (cob.monitoradas && cob.universo_total
                        ? ' (' + cob.monitoradas + ' de ' + cob.universo_total + ' companhias)' : '')
                      + ': a média do setor no universo é, por construção, idêntica à da carteira, e a coluna só teria '
                      + 'zeros. Ela reaparece sozinha no dia em que o universo for maior que a carteira.'}
                  </p>
                ) : null}
                <p className="legenda" style={{ marginTop: 8 }}>
                  {'Classificação Risk: ' + (aud.n_classificadas || 0) + ' companhias, '
                    + (aud.n_setores_fonte || 0) + ' setores na fonte agrupados em ' + (aud.n_setores_risk || 0)
                    + ' setores Risk — ' + (reg.direto || 0) + ' por correspondência direta, '
                    + (reg.holding_dissolvida || 0) + ' holdings dissolvidas na operação, '
                    + (reg.excecao || 0) + ' por enquadramento manual da atividade (as que a fonte devolve sem setor) e '
                    + (reg.desconhecido || 0) + ' sem enquadramento. '
                    + 'Setores com menos de ' + (aud.n_minimo_comparavel || 0) + ' empresas no universo não entram na comparação.'}
                </p>
              </div>
            </div>
          </section>
        );
      })()}
      <section style={{ paddingTop: 0 }}>
        <div className="wrap grade g3" style={{ alignItems: 'start' }}>
          <div className="card">
            <h3>Maior risco da carteira</h3>
            <div>{destaques.piores.length ? destaques.piores.map((e, i) => <LinhaEmp e={e} key={i} />) : vazio}</div>
          </div>
          <div className="card">
            <h3>Menor risco da carteira</h3>
            <div>{destaques.melhores.length ? destaques.melhores.map((e, i) => <LinhaEmp e={e} key={i} />) : vazio}</div>
          </div>
          <div className="card">
            <h3>Movimento de rating</h3>
            <p className="legenda" style={{ margin: '-6px 0 4px' }}>Desde a apuração anterior.</p>
            <div>
              {mv.length ? (
                <>
                  {mv.slice(0, TETO_MV).map((m, i) => (
                    <div className={cls('linha', m.cnpj)} key={i} {...abrir(m.cnpj)}>
                      <div className="esq">
                        <div className="t">{m.denom}</div>
                        <div className="s">{(m.direcao === 'piora' ? 'Rebaixamento' : 'Elevação') + ' de ' + (m.degraus || 1) + ' degrau(s)'}</div>
                      </div>
                      <div className="dir">
                        <span className={'seta ' + m.direcao}>{m.de + (m.direcao === 'piora' ? ' ↓ ' : ' ↑ ') + m.para}</span>
                      </div>
                    </div>
                  ))}
                  {mv.length > TETO_MV ? (
                    <p className="legenda">{'e mais ' + (mv.length - TETO_MV) + ' movimento(s) — '
                      + destaques.piora + ' rebaixamento(s) e ' + destaques.melhora + ' elevação(ões) no total.'}</p>
                  ) : null}
                </>
              ) : <p className="legenda">Nenhuma alteração de rating entre as duas últimas apurações.</p>}
            </div>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <button type="button" className="abrir" aria-expanded={lista} onClick={() => setLista((v) => !v)}>
            {'Ver as ' + emp.length + ' companhias monitoradas'}
          </button>
          {lista ? (
            <div style={{ marginTop: 22 }}>
              <div className="card">
                <table>
                  <thead><tr>
                    <th>Companhia</th><th>Setor</th><th className="r">Rating</th>
                    <th className="r">PD 12m</th><th className="r">Percentil no setor</th>
                  </tr></thead>
                  <tbody>
                    {emp.map((e, i) => (
                      <tr key={i} className={cls('', e.cnpj)} {...abrir(e.cnpj)}>
                        <td className="nome">
                          {e.denom}
                          {e.ticker ? <span className="peq" style={{ display: 'inline' }}>{' ' + e.ticker}</span> : null}
                        </td>
                        <td>{e.setor}</td>
                        <td className="r">
                          <span className={'chip' + (IG.indexOf(String(e.rating).toUpperCase()) >= 0 ? '' : ' hy')}>
                            {e.rating || 'N/D'}
                          </span>
                        </td>
                        <td className="r num">{pc(e.pd, 2)}</td>
                        <td className="r num">{e.sector_pct == null ? '—' : e.sector_pct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <div className="rodape">
        <div className="wrap">
          {[
            ['Data-base', dt(auditoria.data_base)],
            ['Apuração', dth(auditoria.apurado_em)],
            ['Versão do motor', auditoria.versao_engine || '—'],
            ['Companhias apuradas', String(auditoria.empresas_apuradas)],
            ['Cenário de correlação', 'ρ = ' + nf(risco.rho, 2)],
          ].map(([b, v], i) => <div key={i}><b>{b}</b><span>{v}</span></div>)}
        </div>
      </div>
    </>
  );
}
